// Sheet full-screen para convertir una IMAGEN GENERADA en un anuncio.
// Se abre desde la action bar (menú "Más") de GeneratedImagesInline.
//
// Reusa la UI del modo anuncio (`AnuncioPanel`: textos + fuente, colores,
// formato, cantidad, instrucción) PERO:
//   • sin la sección "Productos" — la base es la imagen generada, no un
//     producto (AnuncioPanel omite esa sección si no recibe `products`).
//   • la imagen base se muestra grande: en desktop a la IZQUIERDA de las
//     opciones, en mobile ARRIBA.
//
// Generación: mismo webhook `generar_anuncio_v3` pero con
// `modo: 'anuncio_desde_imagen'` y `imagen_generada_id` (en vez de
// `variante_id`). El backend usa esa imagen como referencia.

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, Sparkles } from 'lucide-react';
import {
  AnuncioPanel,
  ANUNCIO_COLORS_MAX,
  type AnuncioFieldState,
} from '../pages/v2/panels/AnuncioPanel';
import { FormatPicker } from '../pages/v2/FormatPicker';
import { getFormatLabel } from '../pages/v2/mockData';
import LoadingAnimation from './LoadingAnimation';
import { postGenerateAd } from '../lib/generateAdRequest';
import { GENERATE_AD_V3_URL } from '../config';
import { preloadGeneratedImages } from '../utils/preloadGeneratedImages';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { useSidebar } from '../context/SidebarContext';

const AUTO_IMAGE_COUNT_MAX = 6;
const FIELD_DEFAULT: AnuncioFieldState = { hidden: false, value: '', font: '' };

// Mismo mapeo slug → aspect ratio que InterfazV2 usa para el modo anuncio.
const formatSlugToAspect = (slug: string | null): string => {
  switch (slug) {
    case 'story':
      return '9:16';
    case 'square':
      return '1:1';
    case 'portrait':
      return '2:3';
    case 'banner':
      return '16:9';
    case 'editorial':
      return '3:2';
    case 'billboard':
      return '21:9';
    case 'post':
    default:
      return '4:5';
  }
};

export interface AnuncioFromImageSheetProps {
  open: boolean;
  onClose: () => void;
  /** Imagen generada base: `id` para el backend + `url` (firmada) para mostrar. */
  image: { id: string; url: string } | null;
  companyId: string;
}

export const AnuncioFromImageSheet: React.FC<AnuncioFromImageSheetProps> = ({
  open,
  onClose,
  image,
  companyId,
}) => {
  const navigate = useNavigate();
  // Ancho del sidebar (desktop) para que el sheet NO tape la navbar: en
  // desktop arranca a la derecha del sidebar; en mobile (sin sidebar) es
  // full-screen. `setMobileBackOverride` engancha el botón "atrás" del
  // header mobile real de la app (mismo que InterfazV2) para que cierre
  // el sheet en vez de navegar.
  const {
    sidebarWidth,
    setMobileBackOverride,
    setMobileTitleOverride,
    mobileTitleOverride,
  } = useSidebar();

  // Refs para que el effect sólo dependa de `open` (no re-correr cuando
  // cambia onClose o el título).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const prevTitleRef = useRef<string | null>(null);

  // Mientras el sheet está abierto: el botón atrás del header mobile de la
  // app cierra el sheet, y el título pasa a "Crear anuncio". Al cerrar se
  // restaura lo previo. (En desktop el header mobile no se renderiza.)
  useEffect(() => {
    if (!open) return;
    prevTitleRef.current = mobileTitleOverride;
    // Doble flecha: el setter de useState trata una función como updater,
    // así que para GUARDAR un handler hay que devolverlo desde el updater.
    setMobileBackOverride(() => () => onCloseRef.current());
    // El título "Crear anuncio" ahora vive en el contenido (left-aligned),
    // así que el header mobile queda sólo con back + menú.
    setMobileTitleOverride('');
    return () => {
      setMobileBackOverride(null);
      setMobileTitleOverride(prevTitleRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const [titulo, setTitulo] = useState<AnuncioFieldState>(FIELD_DEFAULT);
  const [descripcion, setDescripcion] =
    useState<AnuncioFieldState>(FIELD_DEFAULT);
  const [cta, setCta] = useState<AnuncioFieldState>(FIELD_DEFAULT);
  const [colors, setColors] = useState<string[]>([]);
  const [brandColors, setBrandColors] = useState<string[]>([]);
  const [formatoVal, setFormatoVal] = useState<string>('post');
  const [cantidad, setCantidad] = useState<number>(1);
  const [otra, setOtra] = useState<string>('');
  const [formatOpen, setFormatOpen] = useState<boolean>(false);
  const [generating, setGenerating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Al abrir: reset de la config + cargar paleta de la marca activa
  // (`brand_profiles.colors`) como default, igual que el modo anuncio.
  useEffect(() => {
    if (!open) return;
    setTitulo(FIELD_DEFAULT);
    setDescripcion(FIELD_DEFAULT);
    setCta(FIELD_DEFAULT);
    setColors([]);
    setFormatoVal('post');
    setCantidad(1);
    setOtra('');
    setError(null);
    let cancelled = false;
    (async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) return;
        const { data: prof } = await supabase
          .from('profiles')
          .select('current_brand_id')
          .eq('id', user.id)
          .single();
        const brandId = (prof as { current_brand_id?: string } | null)
          ?.current_brand_id;
        if (!brandId) return;
        const { data: brand } = await supabase
          .from('brand_profiles')
          .select('colors')
          .eq('id', brandId)
          .single();
        const cols = (brand as { colors?: unknown } | null)?.colors;
        if (!cancelled && Array.isArray(cols)) {
          setBrandColors(cols as string[]);
        }
      } catch {
        /* noop — sin paleta, el user puede elegir colores a mano */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  if (!open || !image) return null;

  const buildTexto = (f: AnuncioFieldState): Record<string, unknown> => {
    if (f.hidden) return { incluir: false };
    const trimmed = f.value.trim();
    const base: Record<string, unknown> = trimmed
      ? { incluir: true, modo: 'custom', texto: trimmed }
      : { incluir: true, modo: 'auto' };
    if (f.font && f.font.trim()) base.fuente = f.font.trim();
    return base;
  };

  const creditCost = Math.max(1, Math.min(AUTO_IMAGE_COUNT_MAX, cantidad));

  const handleGenerate = async () => {
    if (generating || !image) return;
    setError(null);
    setGenerating(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('No se encontró el usuario');
      const coloresEfectivos =
        colors.length > 0 ? colors : brandColors.slice(0, ANUNCIO_COLORS_MAX);
      const req: Record<string, unknown> = {
        user_id: user.id,
        modo: 'anuncio_desde_imagen',
        // La base del anuncio es la imagen generada, no un producto.
        imagen_generada_id: image.id,
        cantidad_imagenes: creditCost,
        Format: formatSlugToAspect(formatoVal),
        textos: {
          titulo: buildTexto(titulo),
          descripcion: buildTexto(descripcion),
          cta: buildTexto(cta),
        },
        ...(coloresEfectivos.length > 0 ? { colores: coloresEfectivos } : {}),
        ...(otra.trim() ? { instruccion_adicional: otra.trim() } : {}),
        new_ui: true,
        modo_pro: true,
      };
      const data = (await postGenerateAd(req, {
        url: GENERATE_AD_V3_URL,
      })) as {
        imagenes_generadas_ids?: number[];
        id_company?: string;
      } | null;
      const imageIds = data?.imagenes_generadas_ids;
      if (Array.isArray(imageIds) && imageIds.length > 0) {
        const companyIdResult = data?.id_company || companyId;
        let preloadedImages: unknown[] = [];
        try {
          preloadedImages = await preloadGeneratedImages(
            imageIds,
            companyIdResult,
          );
        } catch {
          /* noop — el preload es best-effort */
        }
        try {
          new Audio('/images/clicksound.m4a').play();
        } catch {
          /* noop */
        }
        onClose();
        // Navegamos a la pantalla de resultados con los nuevos ids. El `key`
        // en el state fuerza que la página tome la nueva tanda aunque ya
        // estemos en esa ruta.
        navigate('/redes-sociales/imagenes-generadas', {
          state: {
            imageIds,
            companyId: companyIdResult,
            preloadedImages,
            ts: Date.now(),
          },
        });
      } else {
        throw new Error('No se generaron imágenes');
      }
    } catch (e) {
      console.error('[AnuncioFromImage] generación falló', e);
      setError(
        'Se produjo un error al generar el anuncio. Volvé a intentarlo.',
      );
    } finally {
      setGenerating(false);
    }
  };

  return createPortal(
    <div
      // En mobile el z queda por DEBAJO del header de la app (z-50) para que
      // se vea su top bar real (back + créditos + menú), igual que en
      // InterfazV2. En desktop no hay header mobile, así que sube a z-[120].
      // El FormatPicker y el LoadingAnimation usan su propio portal (z alto),
      // así que no quedan capados por este stacking context.
      className="fixed inset-0 z-40 md:z-[120] bg-white md:bg-[#EFF0F5] flex flex-col md:left-[var(--anuncio-sb)]"
      style={{ ['--anuncio-sb' as string]: `${sidebarWidth}px` } as React.CSSProperties}
    >
      {/* DESKTOP header: Volver (izq) + Generar flotante (der). En mobile el
          back flota sobre la imagen y el Generar es sticky al pie. */}
      <header className="hidden md:flex shrink-0 items-center justify-between gap-3 px-6 pt-4 pb-3">
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center gap-1 text-brand-primary text-[14px] hover:opacity-75 transition-opacity px-3 py-1.5 rounded-full bg-white/85 backdrop-blur-md shadow-sm ring-1 ring-black/[0.04]"
        >
          <ChevronLeft className="w-4 h-4" />
          Volver
        </button>
        <button
          type="button"
          onClick={handleGenerate}
          disabled={generating}
          className="inline-flex items-center gap-2 py-2.5 pl-5 pr-2.5 rounded-full bg-brand-primary hover:bg-brand-primary-hover text-white text-[14.5px] font-semibold shadow-md transition-colors disabled:opacity-60"
        >
          <Sparkles className="w-4 h-4" />
          <span>Generar</span>
          <span className="inline-flex items-center gap-1 bg-white text-brand-black px-2 py-0.5 rounded-full text-[12.5px] font-semibold ml-1">
            <img
              src="/images/token_icon.png"
              alt=""
              aria-hidden="true"
              className="w-3.5 h-3.5 object-contain"
            />
            {creditCost}
          </span>
        </button>
      </header>

      {/* Body scrolleable. Desktop = fila (imagen izq + opciones der).
          Mobile = columna: imagen full-bleed arriba del todo + opciones. */}
      <div className="flex-1 min-h-0 overflow-y-auto pt-[calc(env(safe-area-inset-top,0px)+68px)] md:pt-0 md:px-6 md:pb-8">
        <div className="flex flex-col md:flex-row md:items-start md:justify-center md:gap-6 md:max-w-[1200px] md:mx-auto">
          {/* Mobile: título de la pantalla (left-aligned). En desktop el
              back + Generar viven en el header del sheet. */}
          <h1 className="md:hidden px-4 mb-3 text-[26px] font-semibold text-brand-black/90 leading-tight">
            Crear anuncio
          </h1>
          {/* Columna de la imagen. Mobile: card "Imagen de referencia" con la
              imagen adentro. Desktop: sólo la imagen, sticky a la izquierda. */}
          <div className="relative px-4 md:px-0 md:flex-1 md:max-w-[620px] md:sticky md:top-0 flex items-center justify-center">
            <div className="w-full md:w-auto flex flex-col gap-2 rounded-2xl border border-neutral-200 bg-white p-3 md:gap-0 md:rounded-none md:border-0 md:bg-transparent md:p-0">
              <span className="md:hidden text-[13.5px] text-brand-black/85 leading-tight">
                Imagen de referencia
              </span>
              <img
                src={image.url}
                alt="Imagen de referencia del anuncio"
                className="w-full md:w-auto max-w-full md:max-h-[calc(100vh-9rem)] md:object-contain rounded-xl md:rounded-2xl"
              />
            </div>
          </div>

          {/* Opciones — reusa AnuncioPanel SIN la sección Productos. */}
          <div className="w-full md:w-[440px] md:shrink-0 px-4 md:px-0 pt-4 md:pt-0 pb-4 md:pb-0">
            {/* Desktop: título arriba del container de textos. En mobile el
                título va arriba de la imagen. */}
            <h1 className="hidden md:block mb-4 text-[26px] font-semibold text-brand-black/90 leading-tight">
              Crear anuncio
            </h1>
            {error && (
              <div className="mb-3 rounded-xl bg-red-50 border border-red-200 px-3.5 py-2.5 text-[13px] text-red-700">
                {error}
              </div>
            )}
            <AnuncioPanel
              variant="desktop"
              titulo={titulo}
              onTituloChange={setTitulo}
              descripcion={descripcion}
              onDescripcionChange={setDescripcion}
              cta={cta}
              onCtaChange={setCta}
              colors={colors}
              onColorsChange={setColors}
              brandColors={brandColors}
              autoImageCount={cantidad}
              onAutoImageCountChange={setCantidad}
              autoImageCountMax={AUTO_IMAGE_COUNT_MAX}
              formatoLabel={getFormatLabel(formatoVal)}
              onOpenFormat={() => setFormatOpen(true)}
              otraConfig={otra}
              onOtraChange={setOtra}
              minimalHeaders="always"
            />
          </div>
        </div>
      </div>

      {/* MOBILE: Generar sticky al pie (en desktop va en el header). Mismo
          estilo que el botón Generar del modo anuncio mobile. */}
      <div
        className="md:hidden shrink-0 px-4 pt-3 bg-white/95 backdrop-blur-sm border-t border-black/[0.05]"
        style={{
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
        }}
      >
        <button
          type="button"
          onClick={handleGenerate}
          disabled={generating}
          className="w-full inline-flex items-center justify-center gap-2 py-3.5 px-5 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover text-white text-[15.5px] font-semibold shadow-sm transition-colors disabled:opacity-60"
        >
          <Sparkles className="w-5 h-5" />
          <span>Generar</span>
          <span className="inline-flex items-center gap-1 bg-white text-brand-black px-2 py-0.5 rounded-full text-[12.5px] font-semibold">
            <img
              src="/images/token_icon.png"
              alt=""
              aria-hidden="true"
              className="w-3.5 h-3.5 object-contain"
            />
            {creditCost}
          </span>
        </button>
      </div>

      <FormatPicker
        open={formatOpen}
        selectedId={formatoVal}
        onChange={(id) => setFormatoVal(id)}
        onClose={() => setFormatOpen(false)}
      />

      {generating && (
        <LoadingAnimation
          message="Generando anuncio..."
          subMessage="Esto puede tardar unos segundos"
          durationMs={210_000}
        />
      )}
    </div>,
    document.body,
  );
};

export default AnuncioFromImageSheet;
